"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Menu, Plus, Search } from "lucide-react"
//import { mockClassifications, mockDocumentEvents, mockIntegrations } from "@/lib/mock-org-data"
import type { DocumentClassifier, DocumentEvent, Integration } from "@/types"
import { ClassificationCard } from "@/components/document-classifications/classification-card"
import { AddClassificationDialog } from "@/components/document-classifications/add-classification-dialog"
import { getCSRFToken } from "@/lib/utils"
import { EasyAddClassificationDialog } from "@/components/document-classifications/easy-add-classification-dialog"
import OrgLayout, { useSidebar } from "@/layouts/OrgLayout"
import { createRefreshChannel } from "@/channels/refresh_channel"
import { useRemember } from "@inertiajs/react"
import { useStickyState } from "@/lib/use-sticky"
import { subscribeToDocumentClassifier } from "@/channels/document_classifier_channel"

export interface DocumentClassificationsProps {
  classifications?: DocumentClassifier[]
  documentEvents?: DocumentEvent[]
  integrations?: Integration[]
}

function DocumentClassification({ classifications: initialClassifications, documentEvents: initialDocumentEvents, integrations: initialIntegrations }: DocumentClassificationsProps) {
  const [classifications, setClassifications] = useState<DocumentClassifier[]>(initialClassifications || [])
  console.log("Initial classifications:", initialClassifications)
  const [documentEvents, setDocumentEvents] = useState<DocumentEvent[]>(initialDocumentEvents || [])
  const [integrations] = useState<Integration[]>(initialIntegrations || [])

  const [classificationDialogOpen, setClassificationDialogOpen] = useState(false)
  const [editingClassification, setEditingClassification] = useStickyState<DocumentClassifier | null>(null, 'editing-classification', true)
  console.log("Editing classification:", editingClassification)


  const [easyDialogOpen, setEasyDialogOpen] = useState(false)
  const [editingEasyClassification, setEditingEasyClassification] = useState<{ name: string, classification_description: string } | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    if (editingClassification) {
      const updated = classifications.find(c => c.id === editingClassification.id)
      if (JSON.stringify(updated) !== JSON.stringify(editingClassification)) {
        setEditingClassification(updated || null)
      }
    }
  }, [classifications])

  useEffect(() => {
    const channel = subscribeToDocumentClassifier({
      received: (data: { status: string; classification: DocumentClassifier }) => {
        // status can only be done
        const { document_classifier_id, status } = data
        setClassifications((prev) =>
          prev.map((c) =>
            c.id === document_classifier_id
              ? { ...c, status }
              : c
          )
        )

      }
    })
    return () => {
      channel.unsubscribe()
    }

  }, [])


  const handleSaveClassification = (classification: Partial<DocumentClassifier>) => {

    if (classification.id && editingClassification) {
      // Update existing classification
      const csrfToken = getCSRFToken()
      fetch(`/document_classifier/${classification.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify(classification),
      }).then(response => {
        if (response.ok) {
          console.log("Classification updated successfully")
          setClassifications(
            classifications.map((c) =>
              c.id === classification.id ? { ...classification, updated_at: new Date().toISOString() } as DocumentClassifier : c,
            ),
          )
          setEditingClassification(null)
        } else {
          console.error("Failed to update classification")
        }
      })


    } else {
      const newClassification: Partial<DocumentClassifier> = {
        ...classification,
        id: Date.now(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      const csrfToken = getCSRFToken()
      fetch('/document_classifier', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify(newClassification),
      }).then(response => {
        if (response.ok) {
          console.log("Classification saved successfully")
        } else {
          console.error("Failed to save classification")
        }
      })
    }
    setClassificationDialogOpen(false)
    setEditingClassification(null)
  }
  const handleEasySaveClassification = async ({ name, classification_description }: { name: string, classification_description: string }) => {
    const csrfToken = getCSRFToken()
    const resp = await fetch('/document_classifier/easy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify({ name, classification_description }),
    })
    const data = await resp.json()
    if (resp.ok) {
    } else {
      console.error("Failed to save classification")
    }
    setEasyDialogOpen(false)
    setEditingEasyClassification(null)
  }



  const handleDeleteClassification = (id: number) => {
    const csrfToken = getCSRFToken()
    fetch(`/document_classifier/${id}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken || '',
      },
    }).then(response => {
      if (!response.ok) {
        console.error("Failed to delete classification")
      }
    })
    setClassifications(classifications.filter((c) => c.id !== id))
  }

  const handleUpdateClassification = (classification: DocumentClassifier) => {
    //setClassifications(
    //  classifications.map((c) =>
    //    c.id === classification.id ? { ...classification, updated_at: new Date().toISOString() } : c,
    //  ),
    //)
    setEditingClassification(classification)
  }

  const filteredClassifications = classifications.filter(
    (c) =>
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.classification_prompt?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const { setShowSidebar } = useSidebar()
  return (
    <div className="space-y-6 w-full">
      <div className="flex mt-2 items-center gap-4">
        <Menu className="md:hidden w-12 cursor-pointer"
          onClick={() => setShowSidebar(true)}
        />



        <h2 className="text-2xl font-semibold">Document Classifications</h2>


      </div>
      <div className="flex p-2 gap-4">
        <AddClassificationDialog
          className="ml-auto"
          open={classificationDialogOpen}
          onOpenChange={setClassificationDialogOpen}
          editing={editingClassification}
          onSave={handleSaveClassification}
          trigger={
            <Button onClick={() => setEditingClassification(null)}>
              <Plus className="h-4 w-4" />
              Add Classification
            </Button>
          }
        />

        <EasyAddClassificationDialog
          open={easyDialogOpen}
          onOpenChange={setEasyDialogOpen}
          editing={editingEasyClassification}
          onSave={(classifier) => { handleEasySaveClassification(classifier) }}
          trigger={
            <Button onClick={() => setEditingEasyClassification(null)}>
              <Plus className="h-4 w-4" />
              Easy Add Classification
            </Button>
          }
        />
      </div>

      <div className="flex p-2 items-center gap-2">
        <Input
          placeholder="Search classifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="space-y-4">
        {filteredClassifications.map((classification) => (
          <ClassificationCard
            key={classification.id}

            classification={classification.id === editingClassification?.id ? editingClassification : classification}
            documentEvents={documentEvents}
            integrations={integrations}
            onEdit={() => {
              setEditingClassification(classification)
              setClassificationDialogOpen(true)
            }}
            onDelete={() => handleDeleteClassification(classification.id)}
            onUpdateDocumentEvents={setDocumentEvents}
            onUpdateClassification={handleUpdateClassification}
          />
        ))}
      </div>
    </div>
  )
}

DocumentClassification.layout = (page: React.ReactNode) => <OrgLayout activeSection="document_classifier">{page}</OrgLayout>
export default DocumentClassification
